/**
 * Store policies. Payment companies (JazzCash, Easypaisa) check that a store
 * has privacy, refund and terms pages before approving it. Each store starts
 * with these plain templates and can edit them in Settings → Policies.
 *
 * Lines starting with "# " become headings; blank lines separate paragraphs.
 * {store}, {email}, {phone}, {address} are filled in from Store settings.
 */
export const POLICIES = [
  { type: "privacy", key: "policyPrivacy", title: "Privacy Policy" },
  { type: "refund", key: "policyRefund", title: "Return & Refund Policy" },
  { type: "shipping", key: "policyShipping", title: "Shipping Policy" },
  { type: "terms", key: "policyTerms", title: "Terms & Conditions" },
];

const TEMPLATES = {
  privacy: `{store} respects your privacy. This page explains what we collect and why.

# What we collect
Your name, phone number, email, and delivery address when you place an order. We also keep the items you ordered and how you paid.

# How we use it
To deliver your order, contact you about it, and send order updates. With your consent we may send offers; you can stop them at any time.

# Payments
Online payments are processed on the secure pages of JazzCash or Easypaisa. We never see or store your card number, wallet PIN, or OTP.

# Sharing
We share your name, phone and address only with the courier company that delivers your order, and with the payment company for online payments. We do not sell your data.

# Your choices
To see, correct, or delete your information, contact us at {email} or {phone}.`,

  refund: `We want you to be happy with your order from {store}.

# Returns
You can return an item within 7 days of delivery if it is unused, unwashed, and has its original tags and packaging. Sale items marked "final sale" cannot be returned.

# Damaged or wrong items
If an item arrives damaged or is not what you ordered, contact us within 48 hours of delivery with a photo. We will replace it or refund you at no cost.

# How to return
Contact us at {email} or {phone} with your order number. We will tell you how to send the item back.

# Refunds
After we receive and check the item, we refund within 7 working days:
- Paid online (JazzCash, Easypaisa, card): back to the same account or card.
- Paid cash on delivery: by bank transfer or JazzCash/Easypaisa to an account you choose.

Delivery charges are not refunded unless the item was damaged or wrong.`,

  shipping: `{store} delivers across Pakistan.

# Delivery time
Orders are usually delivered within 3 to 5 working days in major cities and 5 to 7 working days elsewhere.

# Charges
Delivery charges are shown at checkout before you pay.

# Tracking
You receive a tracking number by email once your order ships. You can also follow it on our Track order page.

# Cash on delivery
Please keep the exact amount ready. If a parcel is refused at the door, future cash on delivery orders may need to be paid online.`,

  terms: `By using the {store} website and placing an order, you agree to these terms.

# Orders
An order is confirmed when we accept it. We may cancel an order if an item is out of stock or a price was shown in error; any payment made is refunded in full.

# Prices
Prices are in Pakistani Rupees (PKR) and include applicable taxes unless stated. Delivery charges are shown at checkout.

# Payment
You can pay cash on delivery, by bank transfer, or online with JazzCash or Easypaisa where available. Online payments are processed by those companies on their secure pages.

# Returns
Returns and refunds follow our Return & Refund Policy.

# Contact
{store}, {address}. Email {email}, phone {phone}.`,
};

/** The policy text to show: the store's own version, or the template. */
export function policyText(type, settings = {}) {
  const policy = POLICIES.find((p) => p.type === type);
  if (!policy) return "";
  const own = String(settings?.[policy.key] || "").trim();
  return fillPolicy(own || TEMPLATES[type], settings);
}

export function policyTemplate(type) {
  return TEMPLATES[type] || "";
}

export function fillPolicy(text, settings = {}) {
  const values = {
    store: settings.storeName || "our store",
    email: settings.supportEmail || "our support email",
    phone: settings.supportPhone || settings.whatsappNumber || "our phone number",
    address: [settings.warehouseAddress, settings.warehouseCity].filter(Boolean).join(", ") || "Pakistan",
  };
  return String(text).replace(/\{(store|email|phone|address)\}/g, (_, name) => values[name]);
}
